import { useLocation, useNavigate } from 'react-router-dom';
import { keepAuthorizationIfUnchanged, useAuthSessionMutation } from './lib/authSessionGuard';
import { Suspense, useEffect, useRef, useState } from 'react';
import { clearLocalStorage, Col, getItemWithExpiration, setItemWithExpiration } from './lib/utils';
import { clearServerCache } from './lib/queryClient';
import ModalAddTrainer from './components/ModalAddTrainer/ModalAddTrainer';
import ModalAlertOneButton from './components/modal/ModalAlertOneButton';
import ModalTerm from './components/modal/ModalTerm';
import DemoVersionQuitButton from './components/Button/DemoVersionQuitButton';
import useUsableStatus from './hook/useUsableStatus';
import ModalAlert from './components/modal/ModalAlert';
import Loading from './components/Loading/Loading';
import { createFreeTrialAPI } from './api/charge';
import dayjs from 'dayjs';
import { useRecoilState } from 'recoil';
import { sidebarStatusState } from './atoms/atom';
import Gnb from './components/Gnb/Gnb';
import useScreenWidth from './hook/useScreenWidth';
import RouteGroup from './scenes/RouteGroup';
import useAxiosInterceptor from './hook/useAxiosInterceptor';
import Modal from './components/modal/Modal';
import { Body3Regular, Subtitle1Bold } from './lib/font';
import ModalPopup from './components/modal/ModalPopup';
import ModalCoachPermission from './scenes/ModalCoachPermission';
import ModalTabLockSettings from './components/Settings/ModalTabLockSettings';
import ModalMemberNotificationSettings from './components/Settings/ModalMemberNotificationSettings';
import PayrollAuthGate from './components/PayrollAuthGate';
import DevScreenInfo from './components/DevScreenInfo';
import useSubscriptionPolling from './hook/useSubscriptionPolling';
import ModalUpdatePopupPublicMember, {
    UPDATE_POPUP_DISMISS_KEY,
    UPDATE_POPUP_SEEN_KEY,
} from './components/modal/ModalUpdatePopupPublicMember';
import { UpdatePopupOpenContext } from './components/modal/updatePopupContext';
import { Cookies } from 'react-cookie';
import { AccessModeProvider, CENTER_VIEW_BANNER_HEIGHT } from './context/AccessModeContext';
import CenterViewBanner from './components/CenterView/CenterViewBanner';

// 키에 버전을 포함시켜 다음 업데이트에서는 이전에 "다시 보지 않기"를 누른 사용자에게도
// 새 소식이 한 번 더 보이게 한다. 팝업 내용을 바꿀 때 이 키도 함께 올린다.
//
// 🔴 키는 **슬라이드 옆**(`ModalUpdatePopupPublicMember`)에 있다. 여기 떨어져 있던
//    동안 슬라이드만 바꾸고 키를 잊은 적이 있어서, 같이 움직이도록 옮겼다.
const updatePopupCookies = new Cookies();

/*
 * 「이번 로그인에는 봤다」 기록. 만료 시각을 주지 않아 **세션 쿠키**가 된다 —
 * 브라우저를 닫으면 사라진다. 키 옆의 주석에 왜 쿠키인지 적어 두었다.
 */
const markUpdatePopupSeen = () => {
    updatePopupCookies.set(UPDATE_POPUP_SEEN_KEY, 'true', {
        path: '/',
        secure: process.env.NODE_ENV !== 'development',
    });
};

/*
 * 로그인 화면에 들어설 때만 부른다. 이 한 자리가 「다음 로그인 때 한 번 더」를
 * 성립시킨다 — 여기서 안 지우면 브라우저를 닫기 전까지 영영 안 뜬다.
 */
const forgetUpdatePopupSeen = () => {
    updatePopupCookies.remove(UPDATE_POPUP_SEEN_KEY, { path: '/' });
};

const App = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const isCenterViewMode = authObject?.accessMode === 'view' || authObject?.role === 'center_viewer';
    const [sidebarStatus, setSidebarStatus] = useRecoilState(sidebarStatusState);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    // 권한 모달
    const [isPermissionModal, setIsPermissionModal] = useState<boolean>(false);
    // 탭 잠금 설정 모달
    const [isTabLockModal, setIsTabLockModal] = useState<boolean>(false);
    // 회원 알림 설정 모달 (2차 비밀번호 없음)
    const [isMemberNotificationModal, setIsMemberNotificationModal] = useState<boolean>(false);

    // 공통 alert 창 상태값
    const [isAlert, setIsAlert] = useState<boolean>(false);
    const [alertTitle, setAlertTitle] = useState<string | null>(null);
    const [alertContent, setAlertContent] = useState<string | null>(null);
    const [isError, setIsError] = useState<boolean>(false);
    const [alertButtonContent, setAlertButtonContent] = useState<string | null>(null);
    const [alertCallBack, setAlertCallBack] = useState<any>(null);
    const [isTwoButton, setIsTwoButton] = useState<boolean>(false);
    const [twoButtonContent, setTwoButtonContent] = useState<string | null>(null);
    const [twoButtonCallBack, setTwoButtonCallBack] = useState<any>(null);

    // 공통 2버튼 alert 창 상태값
    const [isAlertTwoButton, setIsAlertTwoButton] = useState<boolean>(false);
    const [alertTwoButtonTitle, setAlertTwoButtonTitle] = useState<string>('취소');
    const [isErrorTwoButton, setIsErrorTwoButton] = useState<boolean>(false);
    const [alertTwoButtonContent, setAlertTwoButtonContent] = useState<string>('');
    const [alertTwoButtonCancelText, setAlertTwoButtonCancelText] = useState<string>('취소');
    const [alertTwoButtonConfirmText, setAlertTwoButtonConfirmText] = useState<string>('확인');
    const [alertTwoButtonConfirmCallBack, setAlertTwoButtonConfirmCallBack] = useState<any>(null);
    const [alertTwoButtonCancelCallBack, setAlertTwoButtonCancelCallBack] = useState<any>(null);
    const [isExitButton, setIsExitButton] = useState<boolean>(false);

    // 모달 스테이트
    const [isAddTrainer, setIsAddTrainer] = useState<boolean>(false);
    const [isTerm, setIsTerm] = useState<boolean>(false);
    const [isFreeTrial, setIsFreeTrial] = useState<boolean>(false);

    const [isVisibleGnb, setIsVisibleGnb] = useState<boolean>(false);
    const [isPopup, setIsPopup] = useState<boolean>(true);
    const [isUpdatePopup, setIsUpdatePopup] = useState<boolean>(false);

    // 공통 모달 state 초기화
    const alertStateReset = () => {
        setIsAlert(false);
        setAlertTitle(null);
        setAlertContent(null);
        setIsError(false);
        setAlertButtonContent(null);
        setAlertCallBack(null);
        // 2버튼 모드 상태도 초기화하지 않으면 다음 1버튼 알럿이 2버튼으로 잔류함
        setIsTwoButton(false);
        setTwoButtonContent(null);
        setTwoButtonCallBack(null);
    };

    /**
     * 공통 버튼 모달
     * @param title : 제목 텍스트
     * @param content : 본문 텍스트
     * @param isError : 아이콘 색여부
     * @param buttonContent : 버튼 텍스트 (선택)
     * @param callBack : 콜백함수 (선택)
     * @param isTwoButton : 두가지 버튼 여부 (선택)
     * @param twoButtonContent : 두번째 버튼 텍스트 (선택)
     * @param twoButtonCallBack : 두번째 버튼 콜백함수 (선택)
     */
    const createAlert = (
        title: string,
        content: string,
        isError: boolean,
        buttonContent?: string,
        callBack?: any,
        isTwoButton?: boolean,
        twoButtonContent?: string,
        twoButtonCallBack?: any,
    ) => {
        setAlertTitle(title);
        setAlertContent(content);
        setIsError(isError);
        buttonContent && setAlertButtonContent(buttonContent);
        callBack && setAlertCallBack(() => callBack);

        isTwoButton && setIsTwoButton(isTwoButton);
        twoButtonContent && setTwoButtonContent(twoButtonContent);
        twoButtonCallBack && setTwoButtonCallBack(twoButtonCallBack);

        setIsAlert(true);
    };

    // 버튼 2개 모달 state 초기화
    const twoButtonAlertStateReset = () => {
        setIsAlertTwoButton(false);
        setAlertTwoButtonTitle('취소');
        setAlertTwoButtonContent('');
        setAlertTwoButtonCancelText('취소');
        setAlertTwoButtonConfirmText('확인');
        setAlertTwoButtonConfirmCallBack(null);
        setAlertTwoButtonCancelCallBack(null);
    };

    /**
     * @param title : 제목 텍스트
     * @param content : 본문 텍스트
     * @param isError : 경고인지 아닌지
     * @param cancelButtonText : 취소버튼 텍스트
     * @param confirmButtonText : 확인버튼 텍스트
     * @param alertTowButtonCancelCallBack : 취소버튼 콜백함수
     * @param alertTwoButtonConfirmCallBack : 확인버튼 콜백함수
     * @param isExitButton : 닫기 버튼 여부
     */
    const createTwoButtonAlert = (
        title: string,
        content: string,
        isError: boolean,
        cancelButtonText?: string,
        confirmButtonText?: string,
        alertTowButtonCancelCallBack?: any,
        alertTwoButtonConfirmCallBack?: any,
        isExitButton = false,
    ) => {
        setAlertTwoButtonTitle(title);
        setAlertTwoButtonContent(content);
        setIsErrorTwoButton(isError);
        cancelButtonText && setAlertTwoButtonCancelText(cancelButtonText);
        confirmButtonText && setAlertTwoButtonConfirmText(confirmButtonText);
        alertTowButtonCancelCallBack && setAlertTwoButtonCancelCallBack(() => alertTowButtonCancelCallBack);
        alertTwoButtonConfirmCallBack && setAlertTwoButtonConfirmCallBack(() => alertTwoButtonConfirmCallBack);
        setIsAlertTwoButton(true);
        setIsExitButton(isExitButton);
    };

    // 트레이너 등록하기 버튼 클릭이벤트
    const onClickAddTrainer = () => {
        if (authObject) {
            // 무료체험 상태일 때
            if (authObject?.usableStatus == 'trial' || authObject?.usableStatus == 'trialPaymentPending') {
                setIsAddTrainer(true);
            }
            // 무료체험 상태가 아닐 때
            else {
                // 강사 인원 수가 플랜 제한 인원수 이상이라면 경고 alert, 추가 가능하다면 트레이너 등록 모달
                authObject?.planLimitCount <= authObject?.coachCount
                    ? createAlert(
                          '오류',
                          '현재플랜 에서의 강사 제한을 초과하였습니다\n 마이페이지 > 플랜정보 에서 플랜을 변경하여 주시길바랍니다',
                          true,
                          '확인',
                          () => navigate('/myPage'),
                      )
                    : setIsAddTrainer(true);
            }
        }
    };

    // Gnb -> 회원 등록 버튼 클릭이벤트
    const onClickMemberRegister = () => {
        navigate('/member/temporary');
    };

    // 이용약관 버튼 클릭이벤트
    const onClickTerm = () => {
        setIsTerm(true);
    };

    // 상단으로 가기 버튼
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        if (location) {
            // url 바뀔때마다 auth 최신화 밑 스크롤 최상단으로
            // 스케줄(캘린더) 관련 페이지는 문서 높이가 급변해 smooth 애니메이션이 스크롤 클램핑에
            // 취소될 수 있으므로(짧은 생성 페이지에선 맨 아래에 남음) 즉시 이동으로 초기화한다
            if (location.pathname.startsWith('/schedule')) {
                window.scrollTo({ top: 0, behavior: 'auto' });
                // 스케줄 화면의 실제 스크롤 컨테이너는 #App이므로 함께 초기화해야 함
                const appEl = document.getElementById('App');
                if (appEl) appEl.scrollTop = 0;
            } else {
                scrollToTop();
            }
            setAuthorization(getItemWithExpiration('authorization'));
            setAuthObject(getItemWithExpiration('authObject'));
            setIsVisibleGnb(true);
            // 공개 페이지에서는 GNB 숨김
            if (
                location.pathname.startsWith('/input-payment') ||
                location.pathname.startsWith('/pay') ||
                location.pathname.startsWith('/gift-pay')
            ) {
                setIsVisibleGnb(false);
            }
            // 슈퍼어드민 페이지에서는 기존 GNB 숨김 (자체 헤더 사용)
            if (location.pathname.startsWith('/superadmin') || location.pathname.startsWith('/center-view')) {
                setIsVisibleGnb(false);
            }
            // 로그인 로그아웃 페이지 진입시 -> 로컬스토리지 초기화, 상태초기화
            // 비로그인 상태의 루트도 URL을 유지한 채 로그인 화면을 보여주므로 동일하게 초기화한다.
            const currentAuthorization = getItemWithExpiration('authorization');
            const currentAuth = getItemWithExpiration('authObject');
            const isUnauthenticatedRoot =
                location.pathname === '/' && (!currentAuthorization || !currentAuth);
            if (location.pathname === '/signIn' || location.pathname === '/signUp' || isUnauthenticatedRoot) {
                // 슈퍼어드민 토큰이 살아있으면 로그인 페이지로 가지 않고 대시보드로 복귀
                if (currentAuth?.role === 'superadmin' && currentAuthorization) {
                    navigate('/superadmin', { replace: true });
                    return;
                }
                clearLocalStorage();
                /*
                 * 🔴 서버 캐시도 함께 버린다 (2026-09-03 대표 제보).
                 *
                 * localStorage 만 비우면 react-query 의 인메모리 캐시에 이전
                 * 계정의 응답이 그대로 남는다. 로그아웃도 로그인도 페이지를
                 * 다시 띄우지 않으므로 그 캐시는 계정 전환을 넘어 살아남고,
                 * centerId 가 없는 키(강사 목록 등)는 다음 계정에서 그대로
                 * 재사용된다 — **다른 센터의 강사가 보인다.**
                 *
                 * 여기서 부르는 이유는 이 시점이 **이전 화면이 이미 언마운트된
                 * 뒤**라서다. 로그아웃 버튼 안에서 부르면 아직 살아 있는 화면이
                 * 이전 토큰으로 다시 받아와 캐시를 도로 채운다
                 * (lib/queryClient.ts 주석 참고).
                 */
                clearServerCache();
                /*
                 * 업데이트 소식 「이번 로그인에는 봤다」 기록도 여기서 지운다.
                 * 이 자리가 곧 「로그아웃 / 세션 만료」다 — 다음 로그인 때
                 * 한 번 더 보여 주려면 여기서 지워야 한다.
                 * (영구 「다시 보지 않기」 쿠키는 건드리지 않는다)
                 */
                forgetUpdatePopupSeen();
                setIsVisibleGnb(false);
                setIsFreeTrial(false);
                setAuthorization(getItemWithExpiration('authorization'));
                setAuthObject(getItemWithExpiration('authObject'));
            }

            // 결제 만료 페이지로 갓을경우 사이드바안보이게
            // 결제 만료 상태가아닌데 직접접근 금지 (슈퍼어드민은 해당없음)
            if (location.pathname.includes('expired') && !location.pathname.startsWith('/superadmin')) {
                if (authObject?.usableStatus === 'needPlanToFree' || authObject?.usableStatus === 'needPlanToCharge') {
                    setIsVisibleGnb(false);
                } else {
                    navigate('/');
                }
            }

            // if (!(location.pathname.includes('daily') || location.pathname.includes('weekly'))) {
            //     setSidebarStatus('open');
            // }
        }
    }, [location]);

    // 윈도우의 뷰포트 에 따른 스크린 타입 감지
    useScreenWidth();
    //  구독 상태에 따른 경고창 페이지 분기
    useUsableStatus({
        oneButtonCallBack: createAlert,
        twoButtonCallBack: createTwoButtonAlert,
        authObject: authObject,
        setAuthObject: setAuthObject,
        alertStateReset: alertStateReset,
        enabled: !isCenterViewMode,
    });
    // axios 요청,응답 전역감지 : 토큰만료시(401,403) 로그인페이지로 리다이렉션
    useAxiosInterceptor({ createAlert, clearLocalStorage, navigate });
    // 구독 상태 30분 간격 polling + 탭 포커스 시 refetch (슈퍼어드민은 구독 없음)
    useSubscriptionPolling({
        authObject,
        setAuthObject,
        authorization,
        enabled: !!authorization && authObject?.role !== 'superadmin' && !isCenterViewMode,
    });

    const isPublicPath =
        location.pathname.startsWith('/input-payment') ||
        location.pathname.startsWith('/setup-payment') ||
        location.pathname.startsWith('/pay') ||
        location.pathname.startsWith('/gift-pay');
    const isSuperAdminPath = location.pathname.startsWith('/superadmin');
    const isCenterViewPath = location.pathname.startsWith('/center-view');

    // API (POST) : 무료체험 생성
    const createFreeTrial = useAuthSessionMutation({
        mutationFn: async () => await createFreeTrialAPI(authorization),
        onSuccess: (res) => {
            if (res.status === 201) {
                const response = res.data;
                const activationDate = response.activationDate;
                const expirationDate = dayjs(response.expirationDate).subtract(1, 'ms').toISOString();
                const expirationDelayDate = dayjs(response.expirationDate)
                    .subtract(1, 'ms')
                    .add(1, 'week')
                    .toISOString();

                const authObjectData = {
                    // 목록에 없는 기존 필드(정산 방식 settlementType·부가세·급여 비밀번호 설정 등)를 버리지 않는다 — 아래 값만 덮어쓴다.
                    ...authObject,
                    adminId: authObject?.adminId,
                    id: authObject?.id,
                    name: authObject?.name,
                    email: authObject?.email,
                    gender: authObject?.gender,
                    phoneNumber: authObject?.phoneNumber,
                    profileImageUrl: authObject?.profileImageUrl,
                    /** 센터 */
                    centerAddress: authObject?.centerAddress,
                    centerCategory: authObject?.centerCategory,
                    centerCode: authObject?.centerCode,
                    centerName: authObject?.centerName,
                    centerId: authObject?.centerId,
                    isNewCommunicationBox: authObject?.isNewCommunicationBox,

                    /** 구독플랜관련 */
                    coachCount: authObject?.coachCount,
                    memberCount: authObject?.memberCount,
                    activationDate: activationDate,
                    expirationDate: expirationDate,
                    expirationDelayDate: expirationDelayDate,
                    finalPaymentDate: authObject?.finalPaymentDate,
                    finalPaymentTryDate: authObject?.finalPaymentTryDate,
                    cardIssuingBank: authObject?.cardIssuingBank,
                    cardNumber: authObject?.cardNumber,
                    planAmount: authObject?.planAmount,
                    usableStatus: 'trial',
                    paymentDueDate: authObject?.paymentDueDate,

                    /** 구독플랜 인원 정보 */
                    planLimitCount: authObject?.planLimitCount, // 플랜 제한 이용 인원
                    prevPlanLimitCount: authObject?.prevPlanLimitCount, // 구독 인원 변경시 변경전 플랜카운트
                    changedPlanLimitCount: authObject?.changedPlanLimitCount, // 구독 인원 변경시 변경 플랜카운트

                    confirmUsableStatus: false,

                    testAccount: authObject?.testAccount,
                };

                // 요청 때 토큰을 다시 쓰던 자리 — 지금 저장소 토큰이 같을 때만(갱신된 새 토큰을 되돌리지 않게).
                keepAuthorizationIfUnchanged(authorization);
                setItemWithExpiration('authObject', authObjectData, 12);
                setAuthObject(authObjectData);
            }
            if (res.status === 400) {
                createAlert('', '이미 7일 무료체험을 이용하고 있습니다', true);
            }
        },
    }, {
        owner: () => ({ authObject, authorization }),
    });

    // 토큰이없이 진입할때면 로그인페이지로 redirect
    // 루트는 URL을 유지한 채 로그인 화면을 렌더링하므로 정확히 '/'일 때만 예외 처리한다.
    useEffect(() => {
        if (
            !authorization &&
            location.pathname !== '/' &&
            !isPublicPath &&
            !isSuperAdminPath &&
            !isCenterViewPath
        ) {
            navigate('/signIn', { replace: true });
        }

        if (authObject) {
            // 슈퍼어드민이 일반 페이지 접근 시 슈퍼어드민 대시보드로 리다이렉트
            if (
                authObject.role === 'superadmin' &&
                !isSuperAdminPath &&
                !isCenterViewPath &&
                !location.pathname.includes('signIn')
            ) {
                navigate('/superadmin', { replace: true });
                return;
            }
            // 슈퍼어드민은 구독 체크 스킵
            if (authObject.role === 'superadmin') {
                setIsFreeTrial(false);
            } else if (!isCenterViewMode && authObject.usableStatus === 'needAction') {
                setIsFreeTrial(true);
                createFreeTrial.mutate();
            } else {
                setIsFreeTrial(false);
            }
        }
    }, [authorization, authObject]);

    // 모바일 기기로 접속시 m.도메인으로 redirect (공개 카드등록 페이지 제외)
    useEffect(() => {
        const isMobile = /iPhone|iPod|Android/i.test(navigator.userAgent);
        if (
            isMobile &&
            !location.pathname.startsWith('/input-payment') &&
            !location.pathname.startsWith('/setup-payment') &&
            !location.pathname.startsWith('/pay') &&
            !location.pathname.startsWith('/gift-pay')
        ) {
            window.location.href = 'https://m.rappomanager.com';
        }
    }, []);

    /*
     * 업데이트 소식 팝업 — **한 로그인에 딱 한 번**.
     *
     * 두 겹으로 막는다. 겹치는 게 아니라 서로 다른 것을 막는다.
     *   ① `prevAuthRef` — 한 번 뜬 뒤 화면을 옮겨도 다시 안 뜨게 (메모리)
     *   ② 세션 쿠키   — **새로고침을 건너뛰어도** 다시 안 뜨게 (브라우저)
     *
     * ①만 있던 동안 「화면 옮길 때마다 뜬다」는 제보가 왔다(2026-09-04).
     * 메모리 변수는 문서가 다시 뜨는 순간 비어서, 스스로 새로고침하는
     * 자리들(정산 비밀번호 취소·저장값 만료·센터 열람 전환)을 지나면
     * 그대로 되살아난다. 쿠키 쪽 주석에 자세히 적어 두었다.
     *
     * 영구 「다시 보지 않기」(체크박스)는 세 번째 층이고 릴리스 단위다.
     */
    const prevAuthRef = useRef<string | null>(null);
    useEffect(() => {
        if (!authorization) {
            prevAuthRef.current = null;
            return;
        }
        if (!authObject) return;

        const wasLoggedOut = !prevAuthRef.current;
        if (!wasLoggedOut) {
            prevAuthRef.current = authorization;
            return;
        }
        prevAuthRef.current = authorization;
        if (isPublicPath || isSuperAdminPath) return;
        if (authObject?.role === 'superadmin' || isCenterViewMode) return;
        // react-cookie 가 JSON 파싱하면서 'true' → boolean true 로 변환되므로 truthy 체크
        if (updatePopupCookies.get(UPDATE_POPUP_DISMISS_KEY)) return;
        // 이번 로그인에 이미 닫았다 — 다음 로그인까지 다시 띄우지 않는다
        if (updatePopupCookies.get(UPDATE_POPUP_SEEN_KEY)) return;
        setIsUpdatePopup(true);
    }, [
        authorization,
        authObject,
        isPublicPath,
        isSuperAdminPath,
        isCenterViewMode,
    ]);

    return (
        <AccessModeProvider authObject={authObject}>
        {/*
          업데이트 소식 팝업이 떠 있는 동안 아침 브리핑 팝업을 잠근다.
          로그인 직후에는 둘 다 뜨는 조건이 동시에 성립한다 (`updatePopupContext`).
        */}
        <UpdatePopupOpenContext.Provider value={isUpdatePopup}>
        <div
            id="App"
            style={{ position: 'relative', paddingTop: isCenterViewMode ? CENTER_VIEW_BANNER_HEIGHT : 0 }}
        >
            {isCenterViewMode && <CenterViewBanner />}
            {/* 상단 메뉴바 (영업팀 관리 페이지에서는 비표시) */}
            {authorization && isVisibleGnb && location.pathname !== '/setup-payment' && (
                <Gnb
                    onClickTerm={onClickTerm}
                    onClickPermissionModal={() => setIsPermissionModal(true)}
                    onClickTabLockModal={() => setIsTabLockModal(true)}
                    onClickMemberNotificationModal={() => setIsMemberNotificationModal(true)}
                    authObject={authObject}
                />
            )}

            <Suspense fallback={<div>Loading...</div>}>
                {/* 페이지 라우팅 */}
                <RouteGroup
                    createAlert={createAlert}
                    onClickTerm={onClickTerm}
                    createTwoButtonAlert={createTwoButtonAlert}
                    setIsLoading={setIsLoading}
                    onClickAddTrainer={onClickAddTrainer}
                    authObject={authObject}
                    setAuthObject={setAuthObject}
                    authorization={authorization}
                    scrollToTop={scrollToTop}
                />
            </Suspense>

            {/* 로딩 */}
            {isLoading && <Loading />}

            {/* 모달 1버튼 : 공통 알럿창 */}
            {isAlert && alertContent && (
                <ModalAlertOneButton
                    title={alertTitle}
                    content={alertContent}
                    isError={isError}
                    buttonContent={alertButtonContent}
                    callBack={alertCallBack}
                    isTwoButton={isTwoButton}
                    twoButtonContent={twoButtonContent}
                    twoButtonCallBack={twoButtonCallBack}
                    alertStateReset={alertStateReset}
                />
            )}

            {/* 모달 2버튼 : 공통알럿 */}
            {isAlertTwoButton && (
                <ModalAlert
                    modalVisible={isAlertTwoButton}
                    setModalVisible={setIsAlertTwoButton}
                    title={alertTwoButtonTitle}
                    content={alertTwoButtonContent}
                    callBack={alertTwoButtonConfirmCallBack}
                    cancelButtonText={alertTwoButtonCancelText}
                    confirmButtonText={alertTwoButtonConfirmText}
                    cancelCallBack={alertTwoButtonCancelCallBack}
                    twoButtonAlertStateReset={twoButtonAlertStateReset}
                    isError={isErrorTwoButton}
                    isExitButton={isExitButton}
                />
            )}

            {/* 모달 : 트레이너 등록하기*/}
            {isAddTrainer && (
                <ModalAddTrainer
                    modalVisible={isAddTrainer}
                    setModalVisible={setIsAddTrainer}
                    authorization={authorization}
                    authObject={authObject}
                    createAlert={createAlert}
                />
            )}

            {/* 모달 : 이용약관 */}
            {isTerm && <ModalTerm modalVisible={isTerm} setModalVisible={setIsTerm} />}

            {/* 모달 : 첫진입시 무료체험 7일 권유 */}
            {/* {isFreeTrial && authorization && (
                    <ModalFreeTrial
                        modalVisible={isFreeTrial}
                        setModalVisible={setIsFreeTrial}
                        createAlert={createAlert}
                        authorization={authorization}
                        authObject={authObject}
                        setAuthObject={setAuthObject}
                    />
                )} */}

            {/* 데모버전 상단 디자인 */}
            {authObject?.testAccount && <DemoVersionQuitButton navigate={navigate} />}

            {/* 페이지 탑 버튼 */}
            {/* {!(location.pathname === '/signIn' || location.pathname === '/signUp') && (
                    <HoverImage
                        src={I_SCROLL_TOP}
                        onClick={scrollToTop}
                        style={{ width: 80, height: 80, position: 'absolute', bottom: 44, right: 88 }}
                    />
                )} */}

            {/* {isPopup && <ModalPopup setIsPopup={setIsPopup} />} */}

            {/* 업데이트 하이라이트 팝업 (기프티콘 안내 2장 + 기존 업데이트) */}
            {isUpdatePopup && (
                <ModalUpdatePopupPublicMember
                    /*
                     * 🔴 **닫는 길이 어느 쪽이든** 「봤다」를 남긴다. X · 「확인했어요」·
                     *    「지금 보러가기」가 전부 이 둘 중 하나로 온다
                     *    (바깥 클릭은 `isCloseOutside={false}` 라 애초에 안 닫힌다).
                     *    하나라도 빠지면 그 길로 닫은 원장님에게만 다시 뜬다.
                     */
                    onClose={() => {
                        markUpdatePopupSeen();
                        setIsUpdatePopup(false);
                    }}
                    onCloseNeverSee={() => {
                        updatePopupCookies.set(UPDATE_POPUP_DISMISS_KEY, 'true', {
                            path: '/',
                            expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
                            secure: process.env.NODE_ENV !== 'development',
                        });
                        markUpdatePopupSeen();
                        setIsUpdatePopup(false);
                    }}
                />
            )}

            {isPermissionModal && (
                <PayrollAuthGate
                    title="권한 설정"
                    sectionKey="coachPermission"
                    onCancel={() => setIsPermissionModal(false)}
                >
                    <ModalCoachPermission setIsVisible={setIsPermissionModal} />
                </PayrollAuthGate>
            )}
            {isTabLockModal && <ModalTabLockSettings setIsVisible={setIsTabLockModal} />}
            {/*
              회원 알림 설정 — PayrollAuthGate 로 감싸지 않는다(설계 Q4: 2차 비밀번호 없음).
              열람 모드는 모달 안에서 읽기 전용으로 처리한다. 닫으면 언마운트돼 다음에 열 때 새로 조회한다.
            */}
            {isMemberNotificationModal && (
                <ModalMemberNotificationSettings setIsVisible={setIsMemberNotificationModal} />
            )}
            {/* <DevScreenInfo /> */}

        </div>
        </UpdatePopupOpenContext.Provider>
        </AccessModeProvider>
    );
};

export default App;
